/* ============================================
   LeadReunionesTab — agenda de la oportunidad

   Dos caminos al mismo lugar: crear la reunión desde
   acá (crea el evento y el Meet en Google) o traer un
   evento que ya existe en el calendario y vincularlo
   al lead.

   La lista la carga el shell —la actividad del
   Overview también la necesita— y acá se gestiona.
   ============================================ */
import { useState } from 'react'
import { useApi } from '@/hooks/useApi'
import {
  IconPlus, IconCalendar, IconClock, IconCheckCircle, IconX, IconExternalLink,
} from '@/components/icons/Icons'

export interface Agendamiento {
  id: number
  fecha: string
  hora_inicio: string
  hora_fin: string
  nombre: string
  email: string
  telefono: string | null
  empresa: string | null
  notas: string | null
  estado: string
  google_meet_url: string | null
  google_event_id: string | null
  origen: string | null
  created_at: string
}

interface GoogleCalendarEvent {
  id: string
  summary: string
  start: string
  end: string
  attendees: { email: string; name?: string }[]
  hangoutLink: string | null
  htmlLink: string | null
}

interface LeadReunionesTabProps {
  leadId: number
  meetings: Agendamiento[]
  loading: boolean
  onReload: () => void
}

type ModalTab = 'new' | 'import'

function estadoInfo(estado: string) {
  switch (estado) {
    case 'confirmado': return { label: 'Confirmada', className: 'ld-meeting-status--confirmed', icon: <IconCheckCircle size={12} /> }
    case 'cancelado': return { label: 'Cancelada', className: 'ld-meeting-status--cancelled', icon: <IconX size={12} /> }
    case 'completado': return { label: 'Completada', className: 'ld-meeting-status--completed', icon: <IconCheckCircle size={12} /> }
    case 'no_show': return { label: 'No asistió', className: 'ld-meeting-status--noshow', icon: <IconX size={12} /> }
    default: return { label: estado, className: '', icon: <IconClock size={12} /> }
  }
}

function horaDe(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch { return '' }
}

function diaDe(iso: string): { day: number; month: string } {
  try {
    const d = new Date(iso)
    return { day: d.getDate(), month: d.toLocaleDateString('es-PE', { month: 'short' }) }
  } catch { return { day: 0, month: '' } }
}

export default function LeadReunionesTab({ leadId, meetings, loading, onReload }: LeadReunionesTabProps) {
  const api = useApi()

  const [abierto, setAbierto] = useState(false)
  const [tab, setTab] = useState<ModalTab>('new')
  const [form, setForm] = useState({ fecha: '', hora_inicio: '09:00', hora_fin: '09:30', notas: '' })
  const [creando, setCreando] = useState(false)
  const [exito, setExito] = useState<{ meet_url: string | null } | null>(null)

  const [gcalEvents, setGcalEvents] = useState<GoogleCalendarEvent[]>([])
  const [cargandoGcal, setCargandoGcal] = useState(false)
  const [errorGcal, setErrorGcal] = useState<string | null>(null)
  const [importando, setImportando] = useState<string | null>(null)

  function abrir() {
    const manana = new Date()
    manana.setDate(manana.getDate() + 1)
    setForm({ fecha: manana.toISOString().split('T')[0], hora_inicio: '09:00', hora_fin: '09:30', notas: '' })
    setExito(null); setGcalEvents([]); setErrorGcal(null); setTab('new')
    setAbierto(true)
  }

  function cerrar() {
    setAbierto(false)
    if (exito) onReload()
  }

  async function crear() {
    if (!form.fecha || !form.hora_inicio || !form.hora_fin) return
    setCreando(true)
    try {
      const res = await api.post<{ ok: boolean; agendamiento: { google_meet_url: string | null } }>('/api/booking/agendar-desde-lead', {
        lead_id: leadId,
        fecha: form.fecha,
        hora_inicio: form.hora_inicio,
        hora_fin: form.hora_fin,
        notas: form.notas.trim() || null,
      })
      setExito({ meet_url: res.agendamiento?.google_meet_url || null })
    } catch (err: any) {
      alert(err?.message || 'Error al crear la reunión')
    } finally {
      setCreando(false)
    }
  }

  async function cargarGcal() {
    setCargandoGcal(true)
    setErrorGcal(null)
    try {
      setGcalEvents(await api.get<GoogleCalendarEvent[]>('/api/booking/google-events/unlinked'))
    } catch (err: any) {
      setErrorGcal(err?.message || 'Error al cargar eventos')
      setGcalEvents([])
    } finally {
      setCargandoGcal(false)
    }
  }

  async function importar(eventId: string) {
    setImportando(eventId)
    try {
      const res = await api.post<{ ok: boolean; agendamiento: { google_meet_url: string | null } }>('/api/booking/import-google-event', {
        lead_id: leadId,
        google_event_id: eventId,
      })
      setExito({ meet_url: res.agendamiento?.google_meet_url || null })
    } catch (err: any) {
      alert(err?.message || 'Error al importar el evento')
    } finally {
      setImportando(null)
    }
  }

  function cambiarTab(nueva: ModalTab) {
    setTab(nueva)
    setExito(null)
    if (nueva === 'import' && gcalEvents.length === 0 && !cargandoGcal) cargarGcal()
  }

  async function cambiarEstado(id: number, estado: 'completado' | 'no_show' | 'cancelado') {
    try {
      await api.put(`/api/booking/agendamientos/${id}`, { estado })
      onReload()
    } catch (err) { console.error('Error actualizando reunión:', err) }
  }

  return (
    <div className="ld-meetings">
      <div className="ld-meetings-header">
        <span />
        <button type="button" className="ld-add-meeting-btn" onClick={abrir}>
          <IconPlus size={13} /> Reunión
        </button>
      </div>

      {loading ? (
        <div className="ld-empty-tab"><p>Cargando reuniones...</p></div>
      ) : meetings.length === 0 ? (
        <div className="ld-empty-tab">
          <IconCalendar size={32} />
          <p>Sin reuniones agendadas</p>
          <button type="button" className="ld-add-meeting-btn" onClick={abrir}>
            <IconPlus size={13} /> Agendar primera reunión
          </button>
        </div>
      ) : (
        <div className="ld-meetings-list">
          {meetings.map(m => {
            const info = estadoInfo(m.estado)
            const fecha = new Date(m.fecha + 'T12:00:00')
            const pasada = fecha < new Date()
            return (
              <div key={m.id} className={`ld-meeting-card${pasada ? ' ld-meeting-card--past' : ''}`}>
                <div className="ld-meeting-card__date">
                  <span className="ld-meeting-card__day">{fecha.getDate()}</span>
                  <span className="ld-meeting-card__month">{fecha.toLocaleDateString('es-PE', { month: 'short' })}</span>
                </div>
                <div className="ld-meeting-card__body">
                  <div className="ld-meeting-card__header">
                    <span className="ld-meeting-card__time"><IconClock size={13} /> {m.hora_inicio} – {m.hora_fin}</span>
                    <span className={`ld-meeting-status ${info.className}`}>{info.icon} {info.label}</span>
                  </div>
                  <div className="ld-meeting-card__title">Sesión de diagnóstico — {m.nombre}</div>
                  {m.notas && <p className="ld-meeting-card__notes">{m.notas}</p>}
                  {m.google_meet_url && (
                    <a href={m.google_meet_url} target="_blank" rel="noopener noreferrer" className="ld-meeting-card__meet">
                      <IconExternalLink size={13} /> Unirse a Google Meet
                    </a>
                  )}
                  {m.estado === 'confirmado' && (
                    <div className="ld-meeting-card__actions">
                      <button type="button" className="ld-meeting-action ld-meeting-action--success" onClick={() => cambiarEstado(m.id, 'completado')}>
                        <IconCheckCircle size={12} /> Asistió
                      </button>
                      <button type="button" className="ld-meeting-action ld-meeting-action--danger" onClick={() => cambiarEstado(m.id, 'no_show')}>
                        <IconX size={12} /> No asistió
                      </button>
                      <button type="button" className="ld-meeting-action ld-meeting-action--muted" onClick={() => cambiarEstado(m.id, 'cancelado')}>
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {abierto && (
        <div className="ld-meeting-modal-overlay" onClick={e => { if (e.target === e.currentTarget) cerrar() }}>
          <div className="ld-meeting-modal">
            <div className="ld-meeting-modal__header">
              <h3 className="ld-meeting-modal__title">{exito ? 'Reunión creada' : 'Agendar reunión'}</h3>
              <button type="button" className="ld-meeting-modal__close" onClick={cerrar}><IconX size={18} /></button>
            </div>

            {!exito && (
              <div className="ld-meeting-modal__tabs">
                <button type="button" className={`ld-meeting-modal__tab${tab === 'new' ? ' ld-meeting-modal__tab--active' : ''}`} onClick={() => cambiarTab('new')}>
                  <IconPlus size={13} /> Nueva
                </button>
                <button type="button" className={`ld-meeting-modal__tab${tab === 'import' ? ' ld-meeting-modal__tab--active' : ''}`} onClick={() => cambiarTab('import')}>
                  <IconCalendar size={13} /> Importar de Calendar
                </button>
              </div>
            )}

            <div className="ld-meeting-modal__body">
              {exito ? (
                <div className="ld-meeting-success">
                  <div className="ld-meeting-success__icon"><IconCheckCircle size={28} /></div>
                  <p className="ld-meeting-success__text">La reunión quedó agendada</p>
                  {exito.meet_url && (
                    <a href={exito.meet_url} target="_blank" rel="noopener noreferrer" className="ld-meeting-success__meet">
                      <IconExternalLink size={13} /> Abrir Google Meet
                    </a>
                  )}
                  <button type="button" className="btn btn-secondary btn-sm ld-meeting-success__cerrar" onClick={cerrar}>Cerrar</button>
                </div>
              ) : tab === 'new' ? (
                <div className="ld-meeting-form">
                  <div className="ld-meeting-form__group">
                    <label className="ld-meeting-form__label">Fecha *</label>
                    <input type="date" className="form-input" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
                  </div>
                  <div className="ld-meeting-form__row">
                    <div className="ld-meeting-form__group">
                      <label className="ld-meeting-form__label">Hora inicio *</label>
                      <input
                        type="time"
                        className="form-input"
                        value={form.hora_inicio}
                        onChange={e => {
                          const inicio = e.target.value
                          setForm(f => {
                            // El fin sigue al inicio con media hora, que es lo que dura.
                            const [h, m] = inicio.split(':').map(Number)
                            const fin = h * 60 + m + 30
                            const hh = Math.floor(fin / 60).toString().padStart(2, '0')
                            const mm = (fin % 60).toString().padStart(2, '0')
                            return { ...f, hora_inicio: inicio, hora_fin: `${hh}:${mm}` }
                          })
                        }}
                      />
                    </div>
                    <div className="ld-meeting-form__group">
                      <label className="ld-meeting-form__label">Hora fin *</label>
                      <input type="time" className="form-input" value={form.hora_fin} onChange={e => setForm(f => ({ ...f, hora_fin: e.target.value }))} />
                    </div>
                  </div>
                  <div className="ld-meeting-form__group">
                    <label className="ld-meeting-form__label">Notas (opcional)</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={form.notas}
                      onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
                      placeholder="Contexto adicional para la reunión..."
                    />
                  </div>
                  <div className="ld-meeting-form__actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={cerrar}>Cancelar</button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={crear}
                      disabled={creando || !form.fecha || !form.hora_inicio || !form.hora_fin}
                    >
                      {creando ? 'Creando...' : 'Crear reunión'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ld-gcal-import">
                  {cargandoGcal ? (
                    <div className="ld-gcal-empty"><p>Cargando eventos de Google Calendar...</p></div>
                  ) : errorGcal ? (
                    <div className="ld-gcal-empty">
                      <p className="ld-gcal-empty__hint">{errorGcal}</p>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={cargarGcal}>Reintentar</button>
                    </div>
                  ) : gcalEvents.length === 0 ? (
                    <div className="ld-gcal-empty">
                      <p className="ld-gcal-empty__hint">No hay eventos futuros sin vincular en tu Google Calendar</p>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={cargarGcal}>Recargar</button>
                    </div>
                  ) : (
                    <div className="ld-gcal-list">
                      {gcalEvents.map(ev => {
                        const d = diaDe(ev.start)
                        return (
                          <div key={ev.id} className={`ld-gcal-event${importando === ev.id ? ' ld-gcal-event--importing' : ''}`}>
                            <div className="ld-gcal-event__date">
                              <span className="ld-gcal-event__day">{d.day}</span>
                              <span className="ld-gcal-event__month">{d.month}</span>
                            </div>
                            <div className="ld-gcal-event__info">
                              <span className="ld-gcal-event__title">{ev.summary}</span>
                              <span className="ld-gcal-event__time">
                                <IconClock size={11} /> {horaDe(ev.start)} – {horaDe(ev.end)}
                              </span>
                              {ev.attendees.length > 0 && (
                                <span className="ld-gcal-event__attendees">
                                  {ev.attendees.map(a => a.name || a.email).join(', ')}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="ld-gcal-event__action"
                              onClick={() => importar(ev.id)}
                              disabled={importando !== null}
                            >
                              {importando === ev.id ? '...' : 'Vincular'}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
